import { useEffect, useRef } from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';

interface UseSceneScrollManagerProps {
  isBeforeMode: boolean;
  baseContentsLength: number;
  isFetchingPreviousPage: boolean;
  contentsLength: number;
}

export const useSceneScrollManager = ({
  isBeforeMode,
  baseContentsLength,
  isFetchingPreviousPage,
  contentsLength
}: UseSceneScrollManagerProps) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const hasScrolledRef = useRef(false);
  const pendingPrependFromBaseLengthRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isBeforeMode) {
      pendingPrependFromBaseLengthRef.current = null;
      return;
    }
    const from = pendingPrependFromBaseLengthRef.current;
    if (from == null) return;
    if (isFetchingPreviousPage) return;

    const now = baseContentsLength;
    if (now <= from) {
      pendingPrependFromBaseLengthRef.current = null;
      return;
    }

    const added = now - from;
    pendingPrependFromBaseLengthRef.current = null;

    const rafId = requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index: added, align: 'start' });
    });
    return () => cancelAnimationFrame(rafId);
  }, [baseContentsLength, isBeforeMode, isFetchingPreviousPage]);

  const markPrependFromBaseLength = () => {
    if (!isBeforeMode) return;
    pendingPrependFromBaseLengthRef.current = baseContentsLength;
  };

  useEffect(() => {
    if (!isBeforeMode || hasScrolledRef.current || contentsLength === 0) return;
    const rafId = requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({ index: contentsLength - 1, align: 'end' });
      hasScrolledRef.current = true;
    });
    return () => cancelAnimationFrame(rafId);
  }, [contentsLength, isBeforeMode]);

  return {
    virtuosoRef,
    hasScrolledRef,
    markPrependFromBaseLength
  };
};
